// Multi-item visual payloads render either as the 2-up contain-fit grid or as an X-style
// carousel: one uniform-height strip of cover-cropped cells scrolled sideways, the cut-off next
// cell doing the "there's more" work (cf. X's multi-video posts).
//
// Attached media — the photos and clips an author uploaded — defaults to the carousel. AI
// generations keep the grid: variants are compared side by side, uncropped. A viewer flips
// either way from a tile's media dropdown; the choice is remembered per post for the session so
// virtualized remounts don't lose it.
//
// Both the renderer (PayloadContainer) and the height estimator (answerPlaceholderHeight) resolve
// the layout and the strip height from here, so the reserved row height stays equal to the
// rendered one. Keep this module free of React and DOM imports: the estimator runs in Node tests.

export type PayloadMediaLayout = 'grid' | 'carousel'

export type PayloadMediaLayoutTile = {
    generated?: boolean | null
}

export const PAYLOAD_MEDIA_CAROUSEL_GAP_PX = 4

// Strip height where no width contract applies (desktop panels keep width-independent media
// geometry, see lib/mediaBuckets): the landscape bucket, so the strip never claims more than a
// single landscape image would.
export const PAYLOAD_MEDIA_CAROUSEL_HEIGHT = 300

// X sizes carousel cells at ~55% of the column width. Wherever the content width is known
// (mobile viewport, reply cards, nested embeds) the strip clamps to that, so a narrow card gets
// X's proportions instead of a 300px-tall sliver of each item.
export const PAYLOAD_MEDIA_CAROUSEL_HEIGHT_WIDTH_RATIO = 0.55
export const MIN_PAYLOAD_MEDIA_CAROUSEL_HEIGHT = 120

// Three or more cells show ~2.4 at once so the third one is visibly cut off; a pair splits the
// strip like the 2-up grid it replaces.
export const PAYLOAD_MEDIA_CAROUSEL_CELL_FRACTION = 0.42

export function payloadMediaCarouselHeight(contentWidth?: number | null): number {
    const width = Number(contentWidth)
    if (!Number.isFinite(width) || width <= 0) return PAYLOAD_MEDIA_CAROUSEL_HEIGHT

    return Math.max(
        MIN_PAYLOAD_MEDIA_CAROUSEL_HEIGHT,
        Math.min(
            PAYLOAD_MEDIA_CAROUSEL_HEIGHT,
            Math.round(width * PAYLOAD_MEDIA_CAROUSEL_HEIGHT_WIDTH_RATIO),
        ),
    )
}

// CSS flex-basis of one cell for a strip of `count` items.
export function payloadMediaCarouselCellBasis(count: number): string {
    if (count <= 2) return `calc(50% - ${PAYLOAD_MEDIA_CAROUSEL_GAP_PX / 2}px)`

    return `${Math.round(PAYLOAD_MEDIA_CAROUSEL_CELL_FRACTION * 1000) / 10}%`
}

export function defaultPayloadMediaLayout(
    tiles: ReadonlyArray<PayloadMediaLayoutTile | null | undefined>,
): PayloadMediaLayout {
    if (tiles.length < 2) return 'grid'

    return tiles.some(tile => Boolean(tile?.generated)) ? 'grid' : 'carousel'
}

const layoutOverrides = new Map<string, PayloadMediaLayout>()
const layoutListeners = new Set<() => void>()

function normalizeLayoutKey(key: unknown): string | null {
    if (key == null) return null

    const normalized = String(key).trim()
    return normalized.length ? normalized : null
}

export function payloadMediaLayoutOverride(key: unknown): PayloadMediaLayout | null {
    const normalized = normalizeLayoutKey(key)
    if (!normalized) return null

    return layoutOverrides.get(normalized) || null
}

export function setPayloadMediaLayoutOverride(
    key: unknown,
    layout: PayloadMediaLayout | null,
): void {
    const normalized = normalizeLayoutKey(key)
    if (!normalized) return

    const current = layoutOverrides.get(normalized) || null
    if (current === layout) return

    if (layout) {
        layoutOverrides.set(normalized, layout)
    } else {
        layoutOverrides.delete(normalized)
    }
    layoutListeners.forEach(listener => listener())
}

export function subscribePayloadMediaLayout(listener: () => void): () => void {
    layoutListeners.add(listener)
    return () => {
        layoutListeners.delete(listener)
    }
}

export function resetPayloadMediaLayoutOverrides(): void {
    if (!layoutOverrides.size) return

    layoutOverrides.clear()
    layoutListeners.forEach(listener => listener())
}

export function resolvePayloadMediaLayout({
    key = null,
    tiles,
}: {
    key?: unknown
    tiles: ReadonlyArray<PayloadMediaLayoutTile | null | undefined>
}): PayloadMediaLayout {
    if (tiles.length < 2) return 'grid'

    return payloadMediaLayoutOverride(key) || defaultPayloadMediaLayout(tiles)
}
